import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { SparklesIcon, Cog6ToothIcon, CheckCircleIcon, ClockIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const MethodSelection = ({ onSelectAI, onSelectManual, dealRoomBrandedName }) => {
  const intl = useIntl();

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="w-full max-w-5xl space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold text-succession-text-primary">
            {intl.formatMessage({ id: 'succession_deals.method_selection.title' }, { name: dealRoomBrandedName })}
          </h1>
          <p className="text-succession-text-secondary text-lg">
            {intl.formatMessage({ id: 'succession_deals.method_selection.subtitle' })}
          </p>
        </div>

        {/* Side-by-Side Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* AI-Guided Path */}
          <div
            className="relative overflow-hidden bg-succession-bg-primary rounded-2xl border-2 border-succession-primary/20 hover:border-succession-primary/40 shadow-succession-md hover:shadow-succession-lg transition-all duration-200 cursor-pointer group"
            onClick={onSelectAI}
          >
            {/* Recommended Badge */}
            <div className="absolute top-0 right-0">
              <div className="flex items-center gap-1 px-3 py-1.5 bg-succession-primary text-white text-sm font-medium rounded-bl-lg">
                <SparklesIcon className="w-3.5 h-3.5" />
                <span>{intl.formatMessage({ id: 'succession_deals.method_selection.ai.recommended' })}</span>
              </div>
            </div>

            <div className="p-8 space-y-6">
              {/* Icon */}
              <div className="w-16 h-16 bg-succession-primary/10 rounded-xl flex items-center justify-center">
                <SparklesIcon className="w-8 h-8 text-succession-primary" />
              </div>

              {/* Title & Description */}
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession_deals.method_selection.ai.title' })}</h2>
                <p className="text-succession-text-secondary">
                  {intl.formatMessage({ id: 'succession_deals.method_selection.ai.description' })}
                </p>
              </div>

              {/* Benefits */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-succession-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession_deals.method_selection.ai.benefit_1.title' })}</p>
                    <p className="text-sm text-succession-text-secondary">
                      {intl.formatMessage({ id: 'succession_deals.method_selection.ai.benefit_1.description' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-succession-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession_deals.method_selection.ai.benefit_2.title' })}</p>
                    <p className="text-sm text-succession-text-secondary">
                      {intl.formatMessage({ id: 'succession_deals.method_selection.ai.benefit_2.description' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-succession-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession_deals.method_selection.ai.benefit_3.title' })}</p>
                    <p className="text-sm text-succession-text-secondary">
                      {intl.formatMessage({ id: 'succession_deals.method_selection.ai.benefit_3.description' })}
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <button
                type="button"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-succession-primary text-white font-semibold rounded-xl hover:bg-succession-primary-hover transition-colors group-hover:shadow-md"
              >
                <span>{intl.formatMessage({ id: 'succession_deals.method_selection.ai.cta' })}</span>
                <ArrowRightIcon className="w-4 h-4" />
              </button>

              {/* Time estimate */}
              <div className="flex items-center justify-center gap-2 text-sm text-succession-text-muted">
                <ClockIcon className="w-4 h-4" />
                <span>{intl.formatMessage({ id: 'succession_deals.method_selection.ai.time_estimate' })}</span>
              </div>
            </div>
          </div>

          {/* Manual Path */}
          <div
            className="relative overflow-hidden bg-succession-bg-primary rounded-2xl border-2 border-succession-border hover:border-succession-border-hover shadow-succession-md hover:shadow-succession-lg transition-all duration-200 cursor-pointer group"
            onClick={onSelectManual}
          >
            <div className="p-8 space-y-6">
              {/* Icon */}
              <div className="w-16 h-16 bg-succession-bg-secondary rounded-xl flex items-center justify-center">
                <Cog6ToothIcon className="w-8 h-8 text-succession-text-primary" />
              </div>

              {/* Title & Description */}
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession_deals.method_selection.manual.title' })}</h2>
                <p className="text-succession-text-secondary">
                  {intl.formatMessage({ id: 'succession_deals.method_selection.manual.description' }, { name: dealRoomBrandedName.toLowerCase() })}
                </p>
              </div>

              {/* Benefits */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-succession-text-muted mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession_deals.method_selection.manual.benefit_1.title' })}</p>
                    <p className="text-sm text-succession-text-secondary">
                      {intl.formatMessage({ id: 'succession_deals.method_selection.manual.benefit_1.description' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-succession-text-muted mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession_deals.method_selection.manual.benefit_2.title' })}</p>
                    <p className="text-sm text-succession-text-secondary">
                      {intl.formatMessage({ id: 'succession_deals.method_selection.manual.benefit_2.description' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-succession-text-muted mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession_deals.method_selection.manual.benefit_3.title' })}</p>
                    <p className="text-sm text-succession-text-secondary">
                      {intl.formatMessage({ id: 'succession_deals.method_selection.manual.benefit_3.description' })}
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <button
                type="button"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-succession-bg-primary text-succession-text-primary font-semibold rounded-xl border-2 border-succession-border hover:border-succession-primary hover:text-succession-primary transition-colors"
              >
                <span>{intl.formatMessage({ id: 'succession_deals.method_selection.manual.cta' })}</span>
                <ArrowRightIcon className="w-4 h-4" />
              </button>

              {/* Time estimate */}
              <div className="flex items-center justify-center gap-2 text-sm text-succession-text-muted">
                <ClockIcon className="w-4 h-4" />
                <span>{intl.formatMessage({ id: 'succession_deals.method_selection.manual.time_estimate' })}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

MethodSelection.propTypes = {
  onSelectAI: PropTypes.func.isRequired,
  onSelectManual: PropTypes.func.isRequired,
  dealRoomBrandedName: PropTypes.string
};

MethodSelection.defaultProps = {
  dealRoomBrandedName: 'Deal Room'
};

export default MethodSelection;
